import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getExercises, deleteExercise } from "../apis/exercisesApi";
import { API_BASE_URL, buildRuntimeExerciseUrl } from "../config/api";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTrash,
  faShare,
  faCheckCircle,
  faRobot,
  faGlobe,
  faUserEdit,
  faTimes,
  faSearch,
  faBars,
  faFileAlt,
  faChevronLeft,
  faChevronRight,
  faTable,
  faThLarge,
  faSort,
  faSortUp,
  faSortDown,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import PublishForm from "./PublishForm";
import GenerateExerciseModal from "./GenerateExerciseModal";
import ThemeToggle from "../components/ThemeToggle";
import { useTranslation } from "react-i18next";
import "../styles.css";

// Safely format an exercise's created/updated date for display.
// Falls back to "—" when the value is missing or cannot be parsed,
// instead of showing the literal text "Invalid Date".
const formatExerciseDate = (value, locale) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Modal component for iframe
const IframeModal = ({ isOpen, onClose, iframeUrl }) => {
  if (!isOpen) return null;

  return (
    <div className="iframe-modal-overlay">
      <div className="iframe-modal">
        <button className="modal-close-button" onClick={onClose} title="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <iframe
          src={iframeUrl}
          title="Exercise Preview"
          className="exercise-iframe"
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      </div>
    </div>
  );
};

const DisplayForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { verifiedToken } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedExerciseType, setSelectedExerciseType] = useState("all");
  const [selectedPublishedStatus, setSelectedPublishedStatus] = useState("all");
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [publishedStatuses, setPublishedStatuses] = useState({});
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [iframeTimestamp, setIframeTimestamp] = useState(Date.now());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const exercisesPerPage = 15;

  // View mode: "table" (default, lightweight/fast) or "grid" (visual thumbnails)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("exerciseViewMode") || "table";
  });

  // Sortable columns state (used by table view)
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  useEffect(() => {
    localStorage.setItem("exerciseViewMode", viewMode);
  }, [viewMode]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  // Fetch drafts from localStorage
  useEffect(() => {
    const draftStr = localStorage.getItem("exerciseDrafts");
    if (draftStr) {
      try {
        const parsedDrafts = JSON.parse(draftStr);
        setDrafts(parsedDrafts.sort((a, b) => b.timestamp - a.timestamp));
      } catch (err) {
        console.error("Error parsing drafts:", err);
        setDrafts([]);
      }
    } else {
      setDrafts([]);
    }
  }, []);

  const checkPublishedStatuses = async (exerciseIds) => {
    try {
      const response = await fetch(`${API_BASE_URL}/checkPublishedExercises`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ exerciseIds }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to check published statuses`);
      }

      const data = await response.json();
      return data.published_statuses || {};
    } catch (err) {
      console.error("Error checking published statuses:", err);
      return {};
    }
  };

  const checkPublishedStatus = async (exerciseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/checkPublishedExercise/${exerciseId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to check published status`);
      }
      const data = await response.json();
      return data.published;
    } catch (err) {
      console.error(`Error checking published status for exercise ${exerciseId}:`, err);
      return false;
    }
  };

  const getUniqueExerciseTypes = () => {
    const predefinedTypes = [
      "dropbox",
      "click",
      "click_3_choix",
      "click_4_choix",
      "click_phrase",
      "click_3_phrase",
      "click_4_phrase",
      "drag_and_drop_phrase",
      "drag_and_drop_paragraphe",
      "clavier_maths",
      "clavier_langue",
      "clavier2inputs",
      "quizz",
    ];
    const exerciseTypes = exercises.map((exercise) => exercise.type);
    const uniqueExerciseTypes = [...new Set(exerciseTypes)];
    const allTypes = [
      ...new Set([...predefinedTypes, ...uniqueExerciseTypes]),
    ].sort();
    return allTypes;
  };

  // Get current page exercises, sorted according to sortConfig (defaults to newest first)
  const getCurrentPageExercises = (allExercises) => {
    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;

    const sorted = [...allExercises].sort((a, b) => {
      let valueA;
      let valueB;

      switch (key) {
        case "name":
          valueA = (a.name || "").toLowerCase();
          valueB = (b.name || "").toLowerCase();
          break;
        case "type":
          valueA = (a.type || "").toLowerCase();
          valueB = (b.type || "").toLowerCase();
          break;
        case "source":
          valueA = (a.source || "").toLowerCase();
          valueB = (b.source || "").toLowerCase();
          break;
        case "published":
          valueA = publishedStatuses[a._id] ? 1 : 0;
          valueB = publishedStatuses[b._id] ? 1 : 0;
          break;
        case "created_at":
        default:
          valueA = new Date(a.created_at).getTime() || 0;
          valueB = new Date(b.created_at).getTime() || 0;
          break;
      }

      if (valueA < valueB) return -1 * dir;
      if (valueA > valueB) return 1 * dir;
      return 0;
    });

    const indexOfLast = currentPage * exercisesPerPage;
    const indexOfFirst = indexOfLast - exercisesPerPage;
    return sorted.slice(indexOfFirst, indexOfLast);
  };

  // Toggle/apply sort for a given column (click again to flip direction)
  const handleSortChange = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "created_at" ? "desc" : "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FontAwesomeIcon icon={faSort} className="sort-icon" />;
    }
    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "asc" ? faSortUp : faSortDown}
        className="sort-icon active"
      />
    );
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredExercises.length / exercisesPerPage);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedExerciseType, selectedPublishedStatus, sortConfig]);

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        setLoading(true);
        const data = await getExercises();
        setExercises(data);
        setFilteredExercises(data);

        // Fetch all published statuses in a single request instead of
        // chunking into batches of 10 — the backend endpoint already
        // accepts an array of any size, so batching only multiplied the
        // number of round-trips for no benefit.
        if (data.length > 0) {
          const allIds = data.map((ex) => ex._id);
          const statuses = await checkPublishedStatuses(allIds);
          setPublishedStatuses((prev) => ({ ...prev, ...statuses }));
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching exercises:", err);
        setError(t("displayForm.errors.fetch_exercises"));
        setLoading(false);
      }
    };

    fetchExercises();
  }, [t]);

  useEffect(() => {
    let filtered = exercises;

    if (selectedExerciseType !== "all") {
      filtered = filtered.filter(
        (exercise) => exercise.type === selectedExerciseType
      );
    }

    if (selectedPublishedStatus !== "all") {
      filtered = filtered.filter((exercise) =>
        selectedPublishedStatus === "published"
          ? publishedStatuses[exercise._id]
          : !publishedStatuses[exercise._id]
      );
    }

    if (searchQuery) {
      filtered = filtered.filter((exercise) =>
        exercise.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredExercises(filtered);
  }, [
    exercises,
    searchQuery,
    selectedExerciseType,
    selectedPublishedStatus,
    publishedStatuses,
  ]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleTypeFilterChange = (e) => {
    setSelectedExerciseType(e.target.value);
  };

  const handlePublishedFilterChange = (e) => {
    setSelectedPublishedStatus(e.target.value);
  };

  const handleEdit = (_id) => {
    navigate(`/update/${_id}`);
  };

  const handleDelete = async (_id) => {
    Swal.fire({
      title: t("delete_confirm.title"),
      text: t("delete_confirm.text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--primary-color)",
      cancelButtonColor: "var(--secondary-color)",
      confirmButtonText: t("delete_confirm.confirm"),
      cancelButtonText: t("delete_confirm.cancel"),
      background: "var(--card-background)",
      customClass: {
        popup: "swal2-popup",
        title: "swal2-title",
        content: "swal2-content",
        confirmButton: "swal2-confirm",
        cancelButton: "swal2-cancel",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteExercise(_id);
          setExercises(exercises.filter((exercise) => exercise._id !== _id));
          setFilteredExercises(
            filteredExercises.filter((exercise) => exercise._id !== _id)
          );
          setPublishedStatuses((prev) => {
            const newStatuses = { ...prev };
            delete newStatuses[_id];
            return newStatuses;
          });
          Swal.fire({
            title: t("delete_success.title"),
            text: t("delete_success.text"),
            icon: "success",
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (err) {
          console.error("Error deleting exercise:", err);
          Swal.fire({
            title: t("delete_error.title"),
            text: t("delete_error.text"),
            icon: "error",
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
        }
      }
    });
  };

  const handlePublish = (exercise) => {
    setSelectedExerciseId(exercise._id);
    setSelectedExercise(exercise);
    setIsPublishModalOpen(true);
  };

  const closePublishModal = async (wasPublished = false) => {
    setIsPublishModalOpen(false);

    if (selectedExerciseId) {
      if (wasPublished) {
        setPublishedStatuses((prev) => ({
          ...prev,
          [selectedExerciseId]: true,
        }));
      } else {
        const isPublished = await checkPublishedStatus(selectedExerciseId);
        setPublishedStatuses((prev) => ({
          ...prev,
          [selectedExerciseId]: isPublished,
        }));
      }
    }

    setSelectedExerciseId(null);
    setSelectedExercise(null);
  };

  const handleCreateNewExercise = () => {
    navigate("/eventform");
  };

  const handleGenerateExerciseClick = () => {
    setIsGenerateModalOpen(true);
  };

  const handleGenerateSuccess = (newExercise) => {
    const exerciseWithSource = {
      ...newExercise,
      source: "openai",
    };
    setExercises((prev) => [...prev, exerciseWithSource]);
    setFilteredExercises((prev) => [...prev, exerciseWithSource]);

    checkPublishedStatus(newExercise._id).then((isPublished) => {
      setPublishedStatuses((prev) => ({
        ...prev,
        [newExercise._id]: isPublished,
      }));
    });
  };

  // Launches an exercise (saved or still a draft/unpublished) through the
  // runtime player. This is used for Play/Test/Preview/Open alike and never
  // triggers a publish, so drafts can be tested without being auto-published.
  const handlePlayClick = (_id, type) => {
    const timestamp = Date.now();
    const url = buildRuntimeExerciseUrl({
      type,
      exerciseId: _id,
      token: verifiedToken,
      version: timestamp,
    });
    setIframeUrl(url);
    setIframeTimestamp(timestamp);
    setIsIframeModalOpen(true);
  };

  const closeIframeModal = () => {
    setIsIframeModalOpen(false);
    setIframeUrl("");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const loadDraft = (draft) => {
    navigate("/eventform", { state: { draft } });
  };

  const deleteDraft = (draftId) => {
    Swal.fire({
      title: t("delete_draft_confirm.title", { defaultValue: "Delete Draft?" }),
      text: t("delete_draft_confirm.text", {
        defaultValue: "Are you sure you want to delete this draft?",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--primary-color)",
      cancelButtonColor: "var(--secondary-color)",
      confirmButtonText: t("delete_confirm.confirm"),
      cancelButtonText: t("delete_confirm.cancel"),
      background: "var(--card-background)",
      customClass: {
        popup: "swal2-popup",
        title: "swal2-title",
        content: "swal2-content",
        confirmButton: "swal2-confirm",
        cancelButton: "swal2-cancel",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedDrafts = drafts.filter((draft) => draft.id !== draftId);
        localStorage.setItem("exerciseDrafts", JSON.stringify(updatedDrafts));
        setDrafts(updatedDrafts);
        Swal.fire({
          title: t("delete_success.title"),
          text: t("delete_draft_success.text", {
            defaultValue: "Draft deleted successfully!",
          }),
          icon: "success",
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers with ellipsis (clean version)
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        end = Math.min(totalPages - 1, 4);
      }

      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }

      if (start > 2) {
        pageNumbers.push('...');
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (end < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const currentExercises = getCurrentPageExercises(filteredExercises);

  if (loading) {
    return (
      <div className="loading-container">
        <p>{t("loading_exercises")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
      </div>
    );
  }

  const uniqueTypes = getUniqueExerciseTypes();

  return (
    <div className="display-form-container">
      <style>
        {`
          .sidebar {
            position: fixed;
            top: 0;
            left: ${isSidebarOpen ? "0" : "-300px"};
            width: 300px;
            height: 100%;
            background: var(--card-background);
            box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
            transition: left 0.3s ease;
            z-index: 1000;
            overflow-y: auto;
            padding: 20px;
          }
          .sidebar-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          .sidebar-title {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--text-color);
          }
          .sidebar-toggle {
            position: fixed;
            top: 20px;
            left: ${isSidebarOpen ? "320px" : "20px"};
            background: var(--primary-color);
            color: white;
            border: none;
            border-radius: 4px;
            padding: 10px;
            cursor: pointer;
            transition: left 0.3s ease;
            z-index: 1001;
          }
          .sidebar-toggle:hover {
            background: #2b6cb0;
          }
          .draft-card {
            background: var(--background);
            border-radius: 8px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            padding: 15px;
            margin-bottom: 20px;
          }
          .draft-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
          }
          .draft-title {
            font-size: 1.2rem;
            font-weight: 500;
            color: var(--text-color);
            cursor: pointer;
          }
          .draft-title:hover {
            text-decoration: underline;
          }
          .draft-type-badge {
            display: inline-block;
            background: var(--primary-color);
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 0.8rem;
          }
          .draft-meta {
            font-size: 0.85rem;
            color: var(--text-secondary);
            margin-top: 5px;
          }
          .draft-actions {
            display: flex;
            align-items: center;
          }
          .draft-actions button {
            background: none;
            border: none;
            cursor: pointer;
            margin-left: 10px;
            color: var(--text-color);
          }
          .draft-actions .delete-draft-button {
            color: #e53e3e;
          }
          .draft-actions .delete-draft-button:hover {
            color: #c53030;
          }
          .no-drafts {
            font-size: 1rem;
            color: var(--text-color);
            text-align: center;
            padding: 20px;
          }
          .content-with-sidebar {
            margin-left: ${isSidebarOpen ? "320px" : "0"};
            transition: margin-left 0.3s ease;
          }
          
          /* ===== BEAUTIFUL PAGINATION CSS from first version ===== */
          .pagination-container {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-top: 30px;
            padding: 20px 0;
            flex-wrap: wrap;
          }
          .pagination-button {
            padding: 8px 16px;
            border: 1px solid var(--border-color);
            border-radius: 4px;
            background: var(--card-background);
            color: var(--text-color);
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 40px;
            text-align: center;
            font-size: 0.9rem;
          }
          .pagination-button:hover:not(:disabled):not(.active) {
            background: var(--primary-color);
            color: white;
            border-color: var(--primary-color);
          }
          .pagination-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .pagination-button.active {
            background: var(--primary-color);
            color: white;
            border-color: var(--primary-color);
            font-weight: 600;
          }
          .pagination-ellipsis {
            padding: 8px 4px;
            color: var(--text-secondary);
            font-size: 0.9rem;
          }
          .pagination-info {
            color: var(--text-secondary);
            font-size: 0.9rem;
            margin: 0 12px;
          }
          .pagination-arrow {
            font-size: 0.8rem;
          }
          @media (max-width: 768px) {
            .pagination-button {
              padding: 6px 12px;
              font-size: 0.85rem;
              min-width: 32px;
            }
            .pagination-info {
              font-size: 0.8rem;
              margin: 0 8px;
            }
            .pagination-ellipsis {
              padding: 6px 2px;
              font-size: 0.8rem;
            }
          }
          .exercises-count {
            color: var(--text-secondary);
            font-size: 0.9rem;
            text-align: center;
            margin-bottom: 15px;
          }
        `}
      </style>
      <button
        onClick={toggleSidebar}
        className="sidebar-toggle"
        title={t("toggle_drafts", { defaultValue: "Toggle Drafts" })}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>
      <div className="sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">{t("drafts", { defaultValue: "Drafts" })}</h3>
        </div>
        {drafts.length > 0 ? (
          drafts.map((draft) => (
            <div key={draft.id} className="draft-card">
              <div className="draft-header">
                <span
                  className="draft-title"
                  onClick={() => loadDraft(draft)}
                  title={t("load_draft", { defaultValue: "Load Draft" })}
                >
                  {draft.exerciseData.name || t("unnamed_draft", { defaultValue: "Unnamed Draft" })}
                </span>
                <div className="draft-actions">
                  <button
                    onClick={() => loadDraft(draft)}
                    title={t("load_draft", { defaultValue: "Load Draft" })}
                  >
                    <FontAwesomeIcon icon={faFileAlt} />
                  </button>
                  <button
                    onClick={() => deleteDraft(draft.id)}
                    className="delete-draft-button"
                    title={t("delete_draft", { defaultValue: "Delete Draft" })}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              <div className="draft-meta">
                <span className="draft-type-badge">
                  {draft.exerciseData.type || t("unknown", { defaultValue: "Unknown" })}
                </span>
                <br />
                <span>
                  {t("saved_at", { defaultValue: "Saved at" })}: {new Date(draft.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="no-drafts">
            {t("no_drafts", { defaultValue: "No drafts available" })}
          </div>
        )}
      </div>
      <div className="content-with-sidebar">
        <div className="display-form-header">
          <div className="header-actions">
            <h2 className="display-form-title">{t("EDIDACT")}</h2>
            <button
              onClick={handleCreateNewExercise}
              className="create-button action-button"
              title={t("createExercise")}
            >
              <span>+</span>
            </button>
            <button
              onClick={handleGenerateExerciseClick}
              className="ai-button action-button"
              title={t("aiGenerate")}
            >
              <FontAwesomeIcon icon={faRobot} />
            </button>
            <ThemeToggle />
            <div className="language-selector">
              <FontAwesomeIcon icon={faGlobe} className="language-icon" />
              <select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="language-select"
                aria-label={t("languages.select_language")}
              >
                <option value="en">{t("languages.en")}</option>
                <option value="fr">{t("languages.fr")}</option>
                <option value="de">{t("languages.de")}</option>
              </select>
            </div>
          </div>
          <div className="search-filter-container">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t("searchPlaceholder")}
              className="search-input"
            />
            <select
              value={selectedExerciseType}
              onChange={handleTypeFilterChange}
              className="type-filter-select"
              aria-label={t("select_exercise_type", {
                defaultValue: "Select exercise type",
              })}
            >
              <option value="all">
                {t("all_types", { defaultValue: "All Types" })}
              </option>
              {uniqueTypes.map((type) => (
                <option key={type} value={type}>
                  {t(type)}
                </option>
              ))}
            </select>
            <select
              value={selectedPublishedStatus}
              onChange={handlePublishedFilterChange}
              className="published-filter-select"
              aria-label={t("select_published_status", {
                defaultValue: "Select published status",
              })}
            >
              <option value="all">
                {t("all_statuses", { defaultValue: "All Statuses" })}
              </option>
              <option value="published">
                {t("published", { defaultValue: "Published" })}
              </option>
              <option value="unpublished">
                {t("unpublished", { defaultValue: "Unpublished" })}
              </option>
            </select>
            <div className="view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`view-toggle-button ${viewMode === "table" ? "active" : ""}`}
                title={t("table_view", { defaultValue: "Table view" })}
                aria-label={t("table_view", { defaultValue: "Table view" })}
              >
                <FontAwesomeIcon icon={faTable} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`view-toggle-button ${viewMode === "grid" ? "active" : ""}`}
                title={t("grid_view", { defaultValue: "Grid view" })}
                aria-label={t("grid_view", { defaultValue: "Grid view" })}
              >
                <FontAwesomeIcon icon={faThLarge} />
              </button>
            </div>
          </div>
        </div>

        {/* Exercises count */}
        <div className="exercises-count">
          {filteredExercises.length > 0 ? (
            <>
              {t("showing", { defaultValue: "Showing" })} {currentExercises.length} {t("of", { defaultValue: "of" })} {filteredExercises.length} {t("exercises", { defaultValue: "exercises" })}
            </>
          ) : (
            t("no_exercises", { defaultValue: "No exercises found" })
          )}
        </div>

        {currentExercises.length === 0 ? (
          <div className="no-exercises-message">
            <div className="no-exercises-card">
              <h3 className="no-exercises-title">
                {t("no_exercises_found.title", {
                  defaultValue: "No Exercises Found",
                })}
              </h3>
              <p className="no-exercises-description">
                {t("no_exercises_found.description", {
                  defaultValue:
                    "No exercises match your current filters. Try adjusting your search, type, or published status.",
                })}
              </p>
              {(searchQuery ||
                selectedExerciseType !== "all" ||
                selectedPublishedStatus !== "all") && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedExerciseType("all");
                    setSelectedPublishedStatus("all");
                  }}
                  className="clear-filters-button action-button"
                >
                  {t("clear_filters", { defaultValue: "Clear Filters" })}
                </button>
              )}
            </div>
          </div>
        ) : viewMode === "table" ? (
          <div className="exercise-table-wrapper">
            <table className="exercise-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSortChange("name")}>
                    <span>{t("name", { defaultValue: "Name" })}</span>
                    {renderSortIcon("name")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortChange("type")}>
                    <span>{t("type", { defaultValue: "Type" })}</span>
                    {renderSortIcon("type")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortChange("source")}>
                    <span>{t("source", { defaultValue: "Source" })}</span>
                    {renderSortIcon("source")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortChange("published")}>
                    <span>{t("published", { defaultValue: "Published" })}</span>
                    {renderSortIcon("published")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSortChange("created_at")}>
                    <span>{t("created", { defaultValue: "Created" })}</span>
                    {renderSortIcon("created_at")}
                  </th>
                  <th className="actions-th">{t("actions", { defaultValue: "Actions" })}</th>
                </tr>
              </thead>
              <tbody>
                {currentExercises.map((exercise) => (
                  <tr key={exercise._id} className="exercise-table-row">
                    <td className="table-name-cell">{exercise.name}</td>
                    <td>
                      <span className="table-type-badge">{t(exercise.type)}</span>
                    </td>
                    <td>
                      <span className={`table-source-badge ${exercise.source}`}>
                        <FontAwesomeIcon icon={exercise.source === "openai" ? faRobot : faUserEdit} />
                        {exercise.source === "openai" ? t("aiGenerated") : t("manual")}
                      </span>
                    </td>
                    <td className="table-published-cell">
                      {publishedStatuses[exercise._id] ? (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="table-published-icon"
                          title={t("published")}
                        />
                      ) : (
                        <span className="table-not-published">—</span>
                      )}
                    </td>
                    <td className="table-date-cell">
                      {formatExerciseDate(exercise.created_at, i18n.language)}
                    </td>
                    <td>
                      <div className="table-action-buttons">
                        <button
                          onClick={() => handlePlayClick(exercise._id, exercise.type)}
                          className="table-action-button play"
                          title={t("play", { defaultValue: "Play" })}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </button>
                        <button
                          onClick={() => handlePublish(exercise)}
                          className="table-action-button publish"
                          title={t("publish")}
                        >
                          <FontAwesomeIcon icon={faShare} />
                        </button>
                        <button
                          onClick={() => handleEdit(exercise._id)}
                          className="table-action-button edit"
                          title={t("edit")}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                        <button
                          onClick={() => handleDelete(exercise._id)}
                          className="table-action-button delete"
                          title={t("delete")}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
        <div className="exercise-grid">
            {currentExercises.map((exercise) => (
              <div key={exercise._id} className="exercise-card">
                <div className="exercise-header">
                  {publishedStatuses[exercise._id] && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="published-icon"
                      title={t("published")}
                    />
                  )}
                  <div className={`exercise-source-badge ${exercise.source}`}>
                    {exercise.source === "openai" ? (
                      <>
                        <FontAwesomeIcon icon={faRobot} /> {t("aiGenerated")}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUserEdit} /> {t("manual")}
                      </>
                    )}
                  </div>
                  <div className="exercise-type-badge">{t(exercise.type)}</div>
                </div>
                <h3 className="exercise-title">{exercise.name}</h3>
                {exercise.background_image && (
                  <div className="exercise-image-container">
                    <img
                      src={exercise.background_image}
                      alt={`${exercise.name} ${t("background")}`}
                      className="exercise-image"
                      onError={(e) => {
                        e.target.src = "/path/to/fallback-image.jpg";
                      }}
                    />
                    <button
                      onClick={() => handlePlayClick(exercise._id, exercise.type)}
                      className="play-button"
                    >
                      <span>▶</span>
                    </button>
                  </div>
                )}
                <div className="action-buttons">
                  <button
                    onClick={() => handlePublish(exercise)}
                    className="publish-button action-button"
                    title={t("publish")}
                  >
                    <FontAwesomeIcon icon={faShare} />
                  </button>
                  <button
                    onClick={() => handleEdit(exercise._id)}
                    className="edit-button action-button"
                    title={t("edit")}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  <button
                    onClick={() => handleDelete(exercise._id)}
                    className="delete-button action-button"
                    title={t("delete")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
        </div>
        )}

        {/* ===== BEAUTIFUL PAGINATION UI from first version ===== */}
        {filteredExercises.length > exercisesPerPage && (
          <div className="pagination-container">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="pagination-button"
              aria-label={t("previous_page", { defaultValue: "Previous page" })}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="pagination-arrow" />
            </button>

            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">…</span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              )
            ))}

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
              aria-label={t("next_page", { defaultValue: "Next page" })}
            >
              <FontAwesomeIcon icon={faChevronRight} className="pagination-arrow" />
            </button>
          </div>
        )}

        {isPublishModalOpen && (
          <PublishForm
            exerciseId={selectedExerciseId}
            exerciseName={selectedExercise?.name}
            exerciseType={selectedExercise?.type}
            exerciseImage={selectedExercise?.background_image}
            onClose={closePublishModal}
            onPublishSuccess={() => closePublishModal(true)}
          />
        )}
        <GenerateExerciseModal
          isOpen={isGenerateModalOpen}
          onClose={() => setIsGenerateModalOpen(false)}
          onGenerateSuccess={handleGenerateSuccess}
        />
        <IframeModal
          isOpen={isIframeModalOpen}
          onClose={closeIframeModal}
          iframeUrl={iframeUrl}
        />
      </div>
    </div>
  );
};

export default DisplayForm;